import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { LineChart } from '@mui/x-charts/LineChart';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import {
    AlertTriangle,
    BarChart3,
    Check,
    Clock,
    ClipboardList,
    FileText,
    Hourglass,
    LayoutDashboard,
    Package,
    Receipt,
    RefreshCw,
    Wallet,
    Wrench,
    XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { httpClient } from '../../common/httpclient';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import AnimatedNumber, { AnimatedMoney } from '../../component/common/AnimatedNumber';
import './dashboard.css';
import { useAppBarAction, useAppBarSubtitle } from '../../component/layout/AppBarActions';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import { usePermissions } from '../../hooks/usePermissions';
import DesktopPeriodSelector from '../../component/common/DesktopPeriodSelector';
import KpiTickerCard, { KpiDeltaChip } from '../../component/report/KpiTickerCard';
import { KitCard, SecH } from '../../component/common/MobileKit';
import {
    BarsChart,
    HBars,
    Kpi,
    KpiGrid,
    KitEmptyNote,
    PERIOD_OPTIONS,
    PeriodChips,
    detectPeriod,
    formatMoneyShort,
    parseMoney,
    periodRange,
} from '../../component/common/MobileDashboardKit';

// Card header (`.card-head`): 36px icon chip (tinted bg/fg) + title + one
// line subtitle — DESIGN-SYSTEM.md §5.2.
function CardHeadRow({ icon, iconBg, iconFg, title, subtitle, loading }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
            <Box sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: iconBg,
                color: iconFg,
            }}>
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>
                    {title}
                </Typography>
                <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', mt: '1px' }}>
                    {loading ? <Skeleton variant="text" width={140} /> : subtitle}
                </Typography>
            </Box>
        </Box>
    );
}

// `.stat-tile` — circular tinted icon + big neutral number + uppercase
// caption, DESIGN-SYSTEM.md §5.9 KPI grid, styled per
// `prototipo-dashboard-ordens.html` (the big number itself is NOT tinted per
// status there — only the icon chip is — unlike the legacy MUI cards this
// screen replaces).
function StatTile({ icon, iconBg, iconFg, iconBorder, value, label, loading }) {
    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: { xs: '4px', md: 0.75 },
            p: { xs: '8px 4px', md: '10px 8px' },
            minWidth: 0,
            borderRadius: '12px',
            bgcolor: 'background.subtle',
            border: '1px solid',
            borderColor: 'divider',
        }}>
            <Box sx={{
                width: { xs: 28, md: 32 },
                height: { xs: 28, md: 32 },
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: iconBg,
                color: iconFg,
                border: iconBorder ? '1px solid' : 'none',
                borderColor: iconBorder,
            }}>
                {icon}
            </Box>
            {loading ? (
                <Skeleton variant="text" width={36} height={30} />
            ) : (
                <Typography sx={{ fontSize: { xs: '18px', md: '18px' }, fontWeight: 800, letterSpacing: '-0.02em', color: 'text.primary', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
                    {typeof value === 'number' ? <AnimatedNumber value={value} format={String} /> : value}
                </Typography>
            )}
            <Typography sx={{ fontSize: '10.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.03em', color: 'text.secondary' }}>
                {label}
            </Typography>
        </Box>
    );
}

// DESIGN-SYSTEM.md §5.11 empty state — centered dashed-circle icon + bold
// line + smaller instructional line. Local copy of `SaleDashboard.js`'s.
function EmptyMiniState({ icon, title, subtitle }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 3, textAlign: 'center' }}>
            <Box sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                border: '2px dashed',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.disabled',
            }}>
                {icon}
            </Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.secondary' }}>{title}</Typography>
            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>
        </Box>
    );
}

const fmtBp = (bp) => `${((bp || 0) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
const fmtPct = (num, den) => (den > 0 ? `${((num / den) * 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%` : '0,0%');

export default function OrderDashboard() {
    const isMobileShell = useIsMobileShell();
    const theme = useTheme();
    const { hasPermission } = usePermissions();
    // `POST /chart/orderservice/kpi-trend` exige `report.serviceorder` (fv-api/config/router.go);
    // sem ela nem chamamos (um 403 abriria o modal global) — os cards mostram só os totais do dashboard.
    const canKpiTrend = hasPermission('report.serviceorder');
    const [kpiTrend, setKpiTrend] = useState(null);
    const [startDate, setStartDate] = useState(dayjs().startOf('month'));
    const [endDate, setEndDate] = useState(dayjs().endOf('month'));
    const [loading, setLoading] = useState(true);
    const [orderData, setOrderData] = useState({
        CountOS: 0,
        CountNotStarted: 0,
        CountStarted: 0,
        CountFinished: 0,
        CountDelayedOrders: 0,
        CountOSCanceled: 0,
        AvgTimeToFinishOrder: 0,
        AvgCostOS: '0,00',
        ReceiveAmountTotal: '0,00',
        CountOSByHour: {
            Labels: [],
            Values: []
        }
    });

    const fetchOrderData = async () => {
        try {
            const response = await httpClient.get(`/dashboard/order?startDate=${startDate.format('YYYY-MM-DD')}&endDate=${endDate.format('YYYY-MM-DD')}`);
            return response.data || {
                CountOS: 0,
                CountNotStarted: 0,
                CountStarted: 0,
                CountFinished: 0,
                CountDelayedOrders: 0,
                CountOSCanceled: 0,
                AvgTimeToFinishOrder: 0,
                AvgCostOS: '0,00',
                ReceiveAmountTotal: '0,00',
                CountOSByHour: {
                    Labels: [],
                    Values: []
                }
            };
        } catch (error) {
            console.error('Erro ao carregar dados das ordens:', error);
            return {
                CountOS: 0,
                CountNotStarted: 0,
                CountStarted: 0,
                CountFinished: 0,
                CountDelayedOrders: 0,
                CountOSCanceled: 0,
                AvgTimeToFinishOrder: 0,
                AvgCostOS: '0,00',
                ReceiveAmountTotal: '0,00',
                CountOSByHour: {
                    Labels: [],
                    Values: []
                }
            };
        }
    };

    const loadAllData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchOrderData();
            setOrderData(data);
        } catch (error) {
            console.error('Erro ao carregar dados:', error);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    // Variação vs. período anterior + sparklines. Falha (ou falta de permissão)
    // não derruba o dashboard: apenas some a variação.
    const loadKpiTrend = useCallback(async () => {
        if (!canKpiTrend || !startDate?.isValid?.() || !endDate?.isValid?.()) {
            setKpiTrend(null);
            return;
        }
        try {
            const res = await httpClient.post('/chart/orderservice/kpi-trend', {
                start: startDate.format('DD/MM/YYYY'),
                end: endDate.format('DD/MM/YYYY'),
            });
            setKpiTrend(res.status >= 200 && res.status < 300 ? res.data : null);
        } catch (error) {
            setKpiTrend(null);
        }
    }, [canKpiTrend, startDate, endDate]);

    const handleRefresh = async () => {
        await Promise.all([loadAllData(), loadKpiTrend()]);
    };

    useEffect(() => {
        loadKpiTrend();
    }, [loadKpiTrend]);

    useEffect(() => {
        loadAllData();
    }, [loadAllData]);

    // No mobile, o refresh vai para o AppBar (e sai da tela).
    useAppBarAction({
        label: 'Atualizar',
        icon: RefreshCw,
        onClick: handleRefresh,
        disabled: loading,
        spinning: loading,
        enabled: isMobileShell,
    });

    const formatTime = (minutes) => {
        if (minutes > 60) {
            return `${Math.round(minutes / 60)} h`;
        }
        return `${minutes} m`;
    };

    // Mesma condição da versão anterior desta tela (arrays já chegam como
    // `[]` por padrão, então isto só cai no estado vazio quando a API não
    // retorna `CountOSByHour` de todo — preservado como estava).
    useAppBarSubtitle(isMobileShell ? 'Dashboard' : null);

    const hasHourData = orderData.CountOSByHour?.Labels && orderData.CountOSByHour?.Values;

    // Distribuição por status (barra de composição). Percentuais sobre o total de
    // ordens do período; "Em atraso" é sinalizador dentro das abertas (não soma na barra).
    const totalOrders = orderData.CountOS || 0;
    const distItems = [
        { label: 'Não iniciadas', value: orderData.CountNotStarted || 0, color: 'secondary.main' },
        { label: 'Em execução', value: orderData.CountStarted || 0, color: 'warning.main' },
        { label: 'Concluídas', value: orderData.CountFinished || 0, color: 'success.main' },
        { label: 'Canceladas', value: orderData.CountOSCanceled || 0, color: 'text.disabled' },
    ];

    // Hora de pico para o subtítulo do gráfico (a API devolve as horas como string).
    const hourValues = (orderData.CountOSByHour?.Values || []).map((v) => Number(v) || 0);
    const peakMax = Math.max(0, ...hourValues);
    const peakLabel = peakMax > 0 ? orderData.CountOSByHour.Labels[hourValues.indexOf(peakMax)] : null;
    const avgCostItem = `R$ ${orderData.AvgCostOS}`;

    if (isMobileShell) {
        const open = (orderData.CountNotStarted || 0) + (orderData.CountStarted || 0);
        const delayed = orderData.CountDelayedOrders || 0;
        const hourLabels = orderData.CountOSByHour?.Labels || [];
        const hourValues = orderData.CountOSByHour?.Values || [];
        const hourItems = hourLabels.map((label, i) => ({ label: String(label), value: hourValues[i] || 0 }));
        const hasHourBars = hourItems.some((i) => i.value > 0);
        const statusItems = [
            { name: 'Não iniciada', value: orderData.CountNotStarted },
            { name: 'Em execução', value: orderData.CountStarted },
            { name: 'Finalizada', value: orderData.CountFinished },
            { name: 'Em atraso', value: orderData.CountDelayedOrders },
            { name: 'Cancelada', value: orderData.CountOSCanceled },
        ];
        return (
            <PageScaffold title="Dashboard de Ordens" titlePageTarget="Ordens" links={[{ title: "Dashboard", link: "" }]} allowBleed>
                <PeriodChips
                    options={PERIOD_OPTIONS}
                    activeId={detectPeriod(startDate, endDate)}
                    onChange={(id) => { const r = periodRange(id); setStartDate(r.start); setEndDate(r.end); }}
                />
                <KpiGrid>
                    <Kpi label="Abertas" icon="orders" value={open.toLocaleString('pt-BR')} delta={delayed > 0 ? { text: `${delayed} atrasadas`, tone: 'down' } : undefined} loading={loading} />
                    <Kpi label="Finalizadas" icon="check" value={(orderData.CountFinished || 0).toLocaleString('pt-BR')} loading={loading} />
                    <Kpi grad label="Faturado" icon="dollar" value={formatMoneyShort(parseMoney(orderData.ReceiveAmountTotal))} loading={loading} />
                    <Kpi label="Tempo médio" icon="clock" value={formatTime(orderData.AvgTimeToFinishOrder)} loading={loading} />
                </KpiGrid>
                <SecH title="OS por status" />
                <KitCard><HBars items={statusItems} /></KitCard>
                <SecH title="Ordens por hora" />
                <KitCard>
                    {hasHourData && hasHourBars ? <BarsChart items={hourItems} color="alt" /> : <KitEmptyNote>Nenhum dado disponível</KitEmptyNote>}
                </KitCard>
            </PageScaffold>
        );
    }

    return (
        <PageScaffold title="Dashboard de Ordens" titlePageTarget="Ordens" links={[{
            title: "Dashboard",
            link: ""
        }]}>
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* page-head — ícone + título + descrição à esquerda; período + atualizar à direita
                    (DESIGN-SYSTEM.md §3.3/§6.1; prototipo-dashboard-ordens.html). */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                        <Box sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '11px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            background: (t) => t.palette.gradient.brand,
                        }}>
                            <LayoutDashboard size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Dashboard de Ordens de Serviço
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Volume por status, indicadores financeiros com variação e distribuição por hora.
                            </Typography>
                        </Box>
                    </Box>

                    <DesktopPeriodSelector
                        start={startDate}
                        end={endDate}
                        onChange={(s, e) => { setStartDate(s); setEndDate(e); }}
                        onRefresh={handleRefresh}
                        loading={loading}
                        dualMonth
                    />
                </Box>

                {/* Status das Ordens — stat-grid (6 tiles) + barra de distribuição. */}
                <Card sx={{ mb: 2 }}>
                    <CardContent sx={{ p: 2.25 }}>
                        <CardHeadRow
                            icon={<ClipboardList size={18} strokeWidth={2} />}
                            iconBg="info.bg"
                            iconFg="info.main"
                            title="Status das Ordens"
                            subtitle="Distribuição das ordens abertas no período"
                            loading={loading}
                        />
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(6, 1fr)' },
                            gap: { xs: 0.75, md: 1.25 },
                        }}>
                            <StatTile icon={<FileText size={18} strokeWidth={2} />} iconBg="info.bg" iconFg="info.main" value={orderData.CountOS} label="Total de ordens" loading={loading} />
                            <StatTile icon={<Hourglass size={18} strokeWidth={2} />} iconBg="secondary.bg" iconFg="secondary.main" value={orderData.CountNotStarted} label="Não iniciadas" loading={loading} />
                            <StatTile icon={<Wrench size={18} strokeWidth={2} />} iconBg="warning.bg" iconFg="warning.main" value={orderData.CountStarted} label="Em execução" loading={loading} />
                            <StatTile icon={<Check size={18} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main" value={orderData.CountFinished} label="Concluídas" loading={loading} />
                            <StatTile icon={<AlertTriangle size={18} strokeWidth={2} />} iconBg="error.bg" iconFg="error.main" value={orderData.CountDelayedOrders} label="Em atraso" loading={loading} />
                            <StatTile icon={<XCircle size={18} strokeWidth={2} />} iconBg="background.paper" iconFg="text.disabled" iconBorder="divider" value={orderData.CountOSCanceled} label="Canceladas" loading={loading} />
                        </Box>

                        {!loading && totalOrders > 0 && (
                            <>
                                <Box title="Composição do total de ordens" sx={{ display: 'flex', height: 8, borderRadius: '6px', overflow: 'hidden', gap: '2px', bgcolor: 'background.subtle', mt: 2, mb: 1 }}>
                                    {distItems.map((d) => (
                                        <Box key={d.label} sx={{ height: '100%', width: `${Math.min(100, (d.value / totalOrders) * 100)}%`, bgcolor: d.color }} />
                                    ))}
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', fontSize: '11px', color: 'text.secondary' }}>
                                    {distItems.map((d) => (
                                        <Box key={d.label} component="span">
                                            <Box component="i" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '3px', mr: '6px', bgcolor: d.color }} />
                                            {d.label} <Box component="em" sx={{ fontStyle: 'normal', color: 'text.disabled' }}>{fmtPct(d.value, totalOrders)}</Box>
                                        </Box>
                                    ))}
                                    <Box component="span">
                                        <Box component="i" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '3px', mr: '6px', bgcolor: 'error.main' }} />
                                        Em atraso <Box component="em" sx={{ fontStyle: 'normal', color: 'text.disabled' }}>{orderData.CountDelayedOrders || 0} (sinalizador, dentro das abertas)</Box>
                                    </Box>
                                </Box>
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Indicadores do período — cards ticker (prototipo-dashboard-ordens.html).
                    Hero "Faturamento líquido" (6 col x 2 linhas, lg). Custos e descontos são
                    "ruins quando sobem" (`inverse`); tempo médio também. Sem `kpi-trend`
                    (sem permissão/erro) os cards de faturamento/custos mostram "—". */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: '10px' }}>
                    <Typography component="h3" sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>Indicadores do período</Typography>
                    {kpiTrend?.days?.length > 0 && (
                        <Typography sx={{ fontSize: '10.5px', fontWeight: 600, color: 'text.disabled', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Box component="i" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'success.main', display: 'inline-block' }} />
                            {kpiTrend.days.length} {kpiTrend.days.length === 1 ? 'dia' : 'dias'} · vs. {kpiTrend.days.length} {kpiTrend.days.length === 1 ? 'dia anterior' : 'dias anteriores'}
                        </Typography>
                    )}
                </Box>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
                    gap: '10px',
                    mb: 2,
                    '& > *': { gridColumn: { xs: 'span 12', sm: 'span 6', lg: 'span 3' }, minWidth: 0 },
                    '& > :first-of-type': { gridColumn: { xs: 'span 12', lg: 'span 6' }, gridRow: { lg: 'span 2' } },
                    // lg: hero 6x2 + 4 tickers (2x2) ao lado; linha de baixo: Custo total (com sparkline) em 9 col + Tempo médio (valor único) em 3 col
                    '& > :nth-last-of-type(2)': { gridColumn: { lg: 'span 9' } },
                    '& > :last-of-type': { gridColumn: { lg: 'span 3' } },
                }}>
                    <KpiTickerCard
                        hero large cumulative uid="ofl" kind="money"
                        label="Resumo financeiro · Faturamento líquido"
                        icon={<Wallet size={13} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main"
                        data={kpiTrend?.revenue} days={kpiTrend?.days}
                        loading={loading}
                        footer={
                            <>
                                {kpiTrend && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, py: 1 }}>
                                        <Typography sx={{ fontSize: '13px', color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            Descontos concedidos
                                            <Box component="small" sx={{ fontSize: '10.5px', color: 'text.disabled', fontWeight: 500 }}>{fmtBp(kpiTrend.discount_rate?.total)} do bruto</Box>
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <KpiDeltaChip kind="money" inverse data={kpiTrend.discounts} />
                                            <Typography sx={{ fontSize: '15px', fontWeight: 800, color: 'error.main', whiteSpace: 'nowrap' }}>
                                                − {`R$ ${(Math.abs(kpiTrend.discounts?.total || 0) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, pt: kpiTrend ? 1.5 : 0, borderTop: kpiTrend ? '1px solid' : 'none', borderColor: 'divider' }}>
                                    <Typography sx={{ fontSize: '13px', color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Box sx={{ width: 26, height: 26, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'success.bg', color: 'success.main' }}>
                                            <Check size={13} strokeWidth={2} />
                                        </Box>
                                        Total recebido
                                        <Box component="small" sx={{ fontSize: '10.5px', color: 'text.disabled', fontWeight: 500 }}>pagamentos lançados nas ordens do período</Box>
                                    </Typography>
                                    <Typography sx={{ fontSize: '16px', fontWeight: 800, color: 'success.main', whiteSpace: 'nowrap' }}>
                                        <AnimatedMoney value={orderData.ReceiveAmountTotal} />
                                    </Typography>
                                </Box>
                            </>
                        }
                    />
                    <KpiTickerCard cumulative uid="oqt" kind="units" label="Ordens faturadas" icon={<ClipboardList size={11} strokeWidth={2} />} iconBg="secondary.bg" iconFg="secondary.main" data={kpiTrend?.orders} days={kpiTrend?.days} loading={loading}
                        note={kpiTrend && 'Ordens pagas criadas no período'} />
                    <KpiTickerCard uid="otk" kind="money" label="Ticket médio" icon={<Receipt size={11} strokeWidth={2} />} iconBg="info.bg" iconFg="brand2.main" data={kpiTrend?.ticket} days={kpiTrend?.days} loading={loading} />
                    <KpiTickerCard cumulative inverse uid="ocp" kind="money" label="Custo de peças" icon={<Package size={11} strokeWidth={2} />} iconBg="warning.bg" iconFg="warning.main" data={kpiTrend?.products_cost} days={kpiTrend?.days} loading={loading} />
                    <KpiTickerCard cumulative inverse uid="oct" kind="money" label="Custo terceirizado" icon={<Wrench size={11} strokeWidth={2} />} iconBg="secondary.bg" iconFg="secondary.main" data={kpiTrend?.external_cost} days={kpiTrend?.days} loading={loading} />
                    <KpiTickerCard cumulative inverse uid="oco" kind="money" label="Custo total" icon={<Package size={11} strokeWidth={2} />} iconBg="error.bg" iconFg="error.main" data={kpiTrend?.total_cost} days={kpiTrend?.days} loading={loading}
                        note={<>Custo médio por item de ordem <b>{avgCostItem}</b></>}
                        footer={!kpiTrend && (
                            <Typography sx={{ fontSize: '10.5px', color: 'text.disabled' }}>
                                Custo médio por item de ordem <b>{avgCostItem}</b>
                            </Typography>
                        )} />
                    <KpiTickerCard uid="otm" kind="minutes" label="Tempo médio" icon={<Clock size={11} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main" value={orderData.AvgTimeToFinishOrder} loading={loading} />
                </Box>

                {/* Ordens Geradas por Hora — panel-head (sem ícone) + LineChart (área). */}
                <Card>
                    <CardContent sx={{ p: 2.25 }}>
                        <Box sx={{ mb: 2 }}>
                            {loading ? (
                                <>
                                    <Skeleton variant="text" width={200} height={22} />
                                    <Skeleton variant="text" width={280} height={16} />
                                </>
                            ) : (
                                <>
                                    <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>
                                        Ordens Geradas por Hora
                                    </Typography>
                                    <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '2px' }}>
                                        Distribuição de abertura de ordens ao longo do dia{peakLabel !== null ? ` · pico às ${parseInt(peakLabel, 10)}h` : ''}
                                    </Typography>
                                </>
                            )}
                        </Box>

                        <Box sx={{ height: 240, width: '100%' }}>
                            {loading ? (
                                <Skeleton variant="rounded" width="100%" height={220} />
                            ) : hasHourData ? (
                                <LineChart
                                    height={220}
                                    margin={{ top: 16, right: 16, bottom: 24, left: 8 }}
                                    hideLegend
                                    grid={{ horizontal: true }}
                                    xAxis={[{
                                        scaleType: 'point',
                                        data: orderData.CountOSByHour?.Labels || [],
                                        disableLine: true,
                                        disableTicks: true,
                                        tickLabelStyle: { fontSize: 10.5, fontWeight: 600, fill: theme.palette.text.disabled },
                                    }]}
                                    yAxis={[{ disableLine: true, disableTicks: true }]}
                                    series={[{
                                        data: orderData.CountOSByHour?.Values || [],
                                        label: 'Total de Ordens',
                                        color: theme.palette.primary.main,
                                        area: true,
                                        showMark: true,
                                        valueFormatter: (value) => `${value ?? 0} ordem(ns)`,
                                    }]}
                                    sx={{
                                        '& .MuiChartsGrid-line': { stroke: theme.palette.divider },
                                        '& .MuiAreaElement-root': { fill: 'url(#orderDashboardAreaGradient)' },
                                        '& .MuiMarkElement-root': {
                                            fill: theme.palette.background.paper,
                                            stroke: theme.palette.primary.main,
                                            strokeWidth: 2,
                                        },
                                    }}
                                >
                                    <defs>
                                        <linearGradient id="orderDashboardAreaGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.35} />
                                            <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                </LineChart>
                            ) : (
                                <EmptyMiniState
                                    icon={<BarChart3 size={24} strokeWidth={2} />}
                                    title="Nenhum dado disponível"
                                    subtitle="Ajuste o período acima para visualizar a distribuição por hora"
                                />
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </PageScaffold>
    )
}
